import { YouTubeClip } from '../domain/entities/MediaContent';

// Using the YT interface from youtube.d.ts instead of redefining it
export class YouTubePlayer {
    private player: any;
    private clip: YouTubeClip;
    private containerId: string;
    private onStateChange: (state: number) => void;
    private isAPIReady: boolean = false;
    private isPlayerReady: boolean = false;

    constructor(
        containerId: string,
        clip: YouTubeClip,
        onStateChange: (state: number) => void
    ) {
        this.containerId = containerId;
        this.clip = clip;
        this.onStateChange = onStateChange;
        this.loadYouTubeAPI();
    }

    private loadYouTubeAPI(): void {
        // Check if API is already loaded
        if (window.YT) {
            this.isAPIReady = true;
            this.initializePlayer();
            return;
        }

        // Load the IFrame Player API code asynchronously
        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        const firstScriptTag = document.getElementsByTagName('script')[0];
        firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);

        // Set up the callback for when API is ready
        window.onYouTubeIframeAPIReady = () => {
            this.isAPIReady = true;
            this.initializePlayer();
        };
    }

    private initializePlayer(): void {
        if (!this.isAPIReady) {
            console.log('YouTube API not ready yet');
            return;
        }

        // Extract video ID from URL
        const videoId = this.extractVideoId(this.clip.url);
        if (!videoId) {
            console.error('Invalid YouTube URL:', this.clip.url);
            return;
        }

        console.log('Initializing YouTube player with video ID:', videoId);

        // Initialize player
        this.player = new window.YT.Player(this.containerId, {
            height: '315',
            width: '560',
            videoId: videoId,
            playerVars: {
                autoplay: this.clip.autoplay ? 1 : 0,
                controls: this.clip.showControls ? 1 : 0,
                start: this.clip.startTime || 0,
                end: this.clip.endTime,
                rel: 0, // Don't show related videos
                modestbranding: 1, // Hide YouTube logo
                playsinline: 1, // Play inline on mobile
                origin: window.location.origin // Add origin for security
            },
            events: {
                onReady: (event: any) => {
                    console.log('YouTube player ready');
                    this.isPlayerReady = true;
                    if (this.clip.autoplay) {
                        event.target.playVideo();
                    }
                },
                onStateChange: (event: any) => {
                    console.log('YouTube player state changed:', event.data);
                    this.onStateChange(event.data);

                    // If video ends and loop is enabled, replay
                    if (event.data === window.YT.PlayerState.ENDED && this.clip.loop) {
                        this.seekTo(this.clip.startTime || 0);
                        this.play();
                    }
                },
                onError: (event: any) => {
                    console.error('YouTube player error:', event.data);
                }
            }
        });
    }

    private extractVideoId(url: string): string | null {
        // Handle youtu.be format
        const shortUrlRegex = /youtu\.be\/([^#\&\?]*)/;
        const shortMatch = url.match(shortUrlRegex);
        if (shortMatch && shortMatch[1]) {
            return shortMatch[1];
        }

        // Handle youtube.com format
        const longUrlRegex = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
        const longMatch = url.match(longUrlRegex);
        if (longMatch && longMatch[2].length === 11) {
            return longMatch[2];
        }

        return null;
    }

    play(): void {
        if (this.isPlayerReady && this.player?.playVideo) {
            this.player.playVideo();
        }
    }

    pause(): void {
        if (this.isPlayerReady && this.player?.pauseVideo) {
            this.player.pauseVideo();
        }
    }

    seekTo(seconds: number): void {
        if (this.isPlayerReady && this.player?.seekTo) {
            this.player.seekTo(seconds, true);
        }
    }

    destroy(): void {
        if (this.isPlayerReady && this.player?.destroy) {
            this.player.destroy();
        }
    }

    playCorrectAnswerAudio(): void {
        if (this.clip.correctAnswerAudioStartTime !== undefined) {
            this.seekTo(this.clip.correctAnswerAudioStartTime);
            this.play();
        }
    }

    public mute(): void {
        if (this.player && this.player.mute) {
            this.player.mute();
        }
    }

    public unMute(): void {
        if (this.player && this.player.unMute) {
            this.player.unMute();
        }
    }
}